/**
 * rondo-web-forms
 *
 * Rondo Web Forms
 *
 * @author Jiri Hybek <jiri@hybek.cz>
 * @license See LICENSE file distributed with this source code.
 */

import { v4 as uuid_v4 } from "uuid";

import { EventEmitter } from "../EventEmitter";
import { FormModel } from "./FormModel";
import { IFormElementPath } from "./IFormElementPath";
import { IFormElementConfig } from "./IFormElementConfig";
import { IFormElementState } from "./IFormElementState";
import { FORM_EL_TYPE } from "./FormElementTypes";

// Event name type
export type TFormElementEventName = "update";

/**
 * Form Element
 *
 * Represents a generic baseline for all form elements such as layout sections, fields, signatures, etc.
 */
export abstract class FormElement<TConfig extends IFormElementConfig> extends EventEmitter<TFormElementEventName> {

	/** Unique element ID */
	protected uid: string;

	/** Element path */
	protected path: IFormElementPath;

	/**
	 * Element constructor
	 *
	 * @param model Model instance
	 * @param parent Parent element
	 * @param type Element type
	 * @param config Element configuration
	 */
	public constructor(
		protected model: FormModel,
		protected parent: FormElement<any>,
		protected type: FORM_EL_TYPE,
		protected config: TConfig
	) {

		super();

		this.uid = uuid_v4();
		this.path = (parent ? parent.getVirtualPath().slice() : []);

		if (config.id || config.title) {

			this.path.push({
				id: config.id || null,
				title: config.title || null
			});

		}

		if (config.tag) {
			model.assignTag(config.tag, this);
		}

	}

	/**
	 * Returns unique element ID
	 */
	public getUid() {

		return this.uid;

	}

	/**
	 * Returns element type
	 */
	public getType() {

		return this.type;

	}

	/**
	 * Returns element parent
	 */
	public getParent<T extends FormElement<any> = FormElement<any>>() {

		return this.parent as T;

	}

	/**
	 * Returns element path
	 */
	public getPath() {

		return this.path;

	}

	/**
	 * Returns element path which should be used for child elements
	 */
	public getVirtualPath() {

		return this.getPath();

	}

	/**
	 * Returns associated model instance
	 */
	public getModel() {

		return this.model;

	}

	/**
	 * Updates the element
	 *
	 * @param resetState If to reset state
	 */
	public update(resetState: boolean = false) {

		// Can be overriden

	}

	/**
	 * Returns element state
	 */
	public getState() : IFormElementState<TConfig> {

		return {
			type: this.type,
			path: this.path,
			config: this.config
		}

	}

	/**
	 * Destroys the element - MUST BE CALLED BY SUB CLASSES
	 */
	public destroy() {

		if (this.config.tag) {
			this.model.unassignTag(this.config.tag, this);
		}

	}

}
